import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '@/utils/helpers';

// GitLab-flavoured Markdown (tables, task lists, strikethrough) as issue text and comments are written.
// Raw HTML in the text is not rendered (react-markdown escapes it) and unsafe link protocols are dropped.
const block: Components = {
  h1: ({ children }) => <h3 className="mb-1 mt-3 text-base font-semibold first:mt-0">{children}</h3>,
  h2: ({ children }) => <h4 className="mb-1 mt-3 text-sm font-semibold first:mt-0">{children}</h4>,
  h3: ({ children }) => <h5 className="mb-1 mt-2 text-sm font-semibold first:mt-0">{children}</h5>,
  h4: ({ children }) => <h6 className="mb-1 mt-2 text-sm font-medium first:mt-0">{children}</h6>,
  p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
  ul: ({ children, className }) => (
    <ul className={cn('mb-2 space-y-0.5 last:mb-0', className?.includes('contains-task-list') ? 'list-none' : 'list-disc ps-5')}>{children}</ul>
  ),
  ol: ({ children }) => <ol className="mb-2 list-decimal space-y-0.5 ps-5 last:mb-0">{children}</ol>,
  a: ({ children, href }) => (
    <a href={href} target="_blank" rel="noreferrer" className="text-primary underline-offset-2 hover:underline">
      {children}
    </a>
  ),
  blockquote: ({ children }) => <blockquote className="mb-2 border-s-2 ps-3 text-muted-foreground last:mb-0">{children}</blockquote>,
  pre: ({ children }) => (
    <pre dir="ltr" className="mb-2 max-h-96 overflow-auto rounded-md bg-muted p-3 text-xs last:mb-0 [&_code]:bg-transparent [&_code]:p-0">
      {children}
    </pre>
  ),
  code: ({ children }) => <code className="rounded bg-muted px-1 py-0.5 font-mono text-[0.85em]">{children}</code>,
  table: ({ children }) => (
    <div className="mb-2 overflow-x-auto last:mb-0">
      <table className="w-full border-collapse text-xs">{children}</table>
    </div>
  ),
  th: ({ children }) => <th className="border px-2 py-1 text-start font-medium">{children}</th>,
  td: ({ children }) => <td className="border px-2 py-1 align-top">{children}</td>,
  hr: () => <hr className="my-3" />,
};

// One line of text (a decision, a progress note): no paragraph breaks around it
const inline: Components = { ...block, p: ({ children }) => <>{children}</> };

export function Markdown({ text, inline: isInline, className }: { text: string; inline?: boolean; className?: string }) {
  const content = (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={isInline ? inline : block}>
      {text}
    </ReactMarkdown>
  );
  return isInline ? (
    <span dir="auto" className={className}>
      {content}
    </span>
  ) : (
    <div dir="auto" className={cn('break-words', className)}>
      {content}
    </div>
  );
}
